<template>
  <div class="inventory-layout">
    <!-- 主内容区 -->
    <main class="inventory-content">
      <!-- 标题和搜索区域 -->
      <div class="content-header">
        <div class="header-left">
          <h1 class="page-title">库存管理</h1>
        </div>
        <div class="search-controls">
          <div class="search-bar">
            <input
              v-model="searchKeyword"
              type="text"
              placeholder="搜索商品名称或条码..."
              class="search-input"
              @keyup.enter="handleSearch"
            />
            <button class="search-button" @click="handleSearch">搜索</button>
          </div>
          <button class="reset-button" @click="resetFilters">重置筛选</button>
        </div>
      </div>

      <!-- 分类筛选 -->
      <div class="filter-section" v-if="categories.length > 0">
        <div class="filter-label">商品分类：</div>
        <div class="category-filters">
          <div
            v-for="category in categories"
            :key="category.id"
            :class="['category-filter', { active: selectedCategory === category.id }]"
            @click="selectCategory(category.id)"
          >
            {{ category.name }}
          </div>
        </div>
      </div>

      <!-- 库存统计 -->
      <div class="inventory-stats">
        <div class="stat-card">
          <div class="stat-label">商品种类</div>
          <div class="stat-value">{{ totalProducts }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">库存总量</div>
          <div class="stat-value">{{ totalStock }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">低库存预警</div>
          <div class="stat-value warning">{{ lowStockCount }}</div>
        </div>
      </div>

      <!-- 库存表格 -->
      <div class="inventory-table-container">
        <table class="inventory-table">
          <thead>
            <tr>
              <th>商品信息</th>
              <th>商品条码</th>
              <th>分类</th>
              <th class="sortable-header" @click="toggleStockSort">
                当前库存
                <span class="sort-indicator">
                  <span v-if="stockSortOrder === 'asc'" class="sort-asc">↑</span>
                  <span v-else-if="stockSortOrder === 'desc'" class="sort-desc">↓</span>
                  <span v-else class="sort-none">↕</span>
                </span>
              </th>
              <th>单价</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="paginatedProducts.length === 0">
              <td colspan="5" class="no-data">暂无库存数据</td>
            </tr>
            <tr v-for="product in paginatedProducts" :key="product.id || Math.random()">
              <td class="product-info">
                <div class="product-name">{{ product.name || '未知商品' }}</div>
              </td>
              <td class="product-code">{{ product.code || 'N/A' }}</td>
              <td class="product-category">
                <span class="category-tag">{{ product.category_name || '未分类' }}</span>
              </td>
              <td class="stock-cell">
                <span :class="['stock-value', { 'low-stock': (product.stock || 0) <= 10 }]">
                  {{ product.stock || 0 }}
                </span>
              </td>
              <td class="price-cell">¥{{ (product.price || 0).toFixed(2) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- 分页 -->
      <div class="pagination">
        <div class="pagination-info">
          显示 {{ products.length > 0 ? (currentPage - 1) * pageSize + 1 : 0 }}
          - {{ Math.min(currentPage * pageSize, products.length) }}
          共 {{ stockStats?.total || products.length || 0 }} 条
        </div>
        <div class="pagination-controls">
          <button
            class="page-btn"
            :disabled="currentPage === 1 || totalPages <= 1"
            @click="currentPage--"
          >
            上一页
          </button>
          <span class="page-number">{{ currentPage }} / {{ Math.max(totalPages, 1) }}</span>
          <button
            class="page-btn"
            :disabled="currentPage >= totalPages || totalPages <= 1"
            @click="currentPage++"
          >
            下一页
          </button>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import { cashierApi } from '../api/cashier.js'

export default {
  name: 'InventoryView',
  props: {
    userInfo: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      searchKeyword: '',
      selectedCategory: 0, // 改为0表示全部，与Home.vue保持一致
      currentPage: 1,
      stockSortOrder: null, // null, 'asc', 'desc'
      pageSize: 15,
      categories: [], // 从API加载分类
      products: [], // 从API加载商品
      stockStats: null // 库存统计信息
    }
  },
  computed: {
    // 直接返回products，因为筛选和分页在服务端完成
    paginatedProducts() {
      return this.products || []
    },
    totalPages() {
      const total = this.stockStats?.total || this.products?.length || 0
      return Math.max(Math.ceil(total / this.pageSize), 1)
    },
    totalStock() {
      // 使用服务端统计的库存总量，如果没有则从当前页面计算
      return (this.products || []).reduce((total, product) => total + (product.stock || 0), 0)
    },
    lowStockCount() {
      // 使用服务端统计的低库存数量，如果没有则从当前页面计算
      if (this.stockStats?.low_stock_count !== undefined) {
        return this.stockStats.low_stock_count
      }
      return (this.products || []).filter(product => (product.stock || 0) <= 10).length
    },
    totalProducts() {
      return this.stockStats?.total || (this.products || []).length
    }
  },
  methods: {
    toggleStockSort() {
      if (this.stockSortOrder === null) {
        this.stockSortOrder = 'asc'
      } else if (this.stockSortOrder === 'asc') {
        this.stockSortOrder = 'desc'
      } else {
        this.stockSortOrder = null
      }
      this.currentPage = 1 // 重置到第一页
      this.loadProducts() // 重新加载数据
    },
    selectCategory(categoryId) {
      this.selectedCategory = categoryId
      this.currentPage = 1
      this.loadProducts() // 重新加载数据
    },
    handleSearch() {
      if (!this.searchKeyword.trim()) {
        alert('请输入搜索关键词')
        return
      }
      this.currentPage = 1
      this.loadProducts() // 重新加载数据
    },
    resetFilters() {
      this.searchKeyword = ''
      this.selectedCategory = 0 // 改为0，表示全部
      this.stockSortOrder = null
      this.currentPage = 1
      this.loadProducts() // 重新加载数据
    },
    getCategoryName(categoryId) {
      const category = this.categories.find(c => c.id === categoryId)
      return category ? category.name : '未分类'
    },

    // 加载商品分类
    async loadCategories() {
      try {
        const result = await cashierApi.getShopCategories()
        if (result.success && result.data) {
          // 添加"全部"分类到最前面
          this.categories = [
            { id: 0, name: '全部', text: '全' },
            ...result.data.map(cat => ({
              id: cat.id,
              name: cat.title,
              text: cat.title.charAt(0)
            }))
          ]
          console.log('商品分类加载成功:', this.categories)
        } else {
          console.error('获取商品分类失败:', result.message)
        }
      } catch (error) {
        console.error('获取商品分类异常:', error)
      }
    },

    // 加载库存商品列表
    async loadProducts() {
      try {
        const params = {
          page: this.currentPage,
          pageSize: this.pageSize
        }

        // 只在有值时才添加参数
        if (this.searchKeyword) {
          params.keyword = this.searchKeyword
        }
        if (this.selectedCategory !== 0) {
          params.categoryId = this.selectedCategory
        }
        if (this.stockSortOrder) {
          params.stockType = this.stockSortOrder === 'asc' ? 'low' : 'normal'
        }

        const result = await cashierApi.getInventoryStock(params)
        if (result.success && result.data) {
          this.products = result.data.list.map(item => {
            const product = item.product
            return {
              id: product.ID,
              name: product.name,
              price: product.normal_price || 0,
              member_price: product.member_price || 0,
              stock: item.quantity,
              category_id: product.category ? product.category.id : 0,
              category_name: product.category ? product.category.title : '',
              is_gift: product.is_gift || false,
              status: product.status || '1',
              code: product.code || product.ID.toString(), // 优先使用商品编码
              text: product.name ? product.name.charAt(0) : '',
              sync_time: item.sync_time,
              sync_by: item.sync_by
            }
          })

          // 更新统计信息
          if (result.data.stats) {
            this.stockStats = result.data.stats
          }

          console.log('库存商品列表加载成功:', this.products)
        } else {
          console.error('获取库存商品列表失败:', result.message)
        }
      } catch (error) {
        console.error('获取库存商品列表异常:', error)
      }
    }
  },
  watch: {
    currentPage() {
      this.loadProducts()
    }
  },
  mounted() {
    // 先加载分类，再加载商品
    this.loadCategories().then(() => {
      this.loadProducts()
    })
  }
}
</script>

<style scoped>
.inventory-layout {
  height: 100vh;
  background-color: #f5f5f5;
  font-family: 'Microsoft YaHei', sans-serif;
}

/* 主内容区 */
.inventory-content {
  flex: 1;
  height: 100vh;
  padding: 20px;
  overflow-y: auto;
  background-color: white;
  box-sizing: border-box;
  min-width: 0; /* 防止flex子项溢出 */
}

.content-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 20px;
  border-bottom: 2px solid #f0f0f0;
}


.page-title {
  margin: 0;
  font-size: 24px;
  font-weight: bold;
  color: #2c3e50;
}

.search-controls {
  display: flex;
  align-items: center;
  gap: 15px;
}

.search-bar {
  display: flex;
  align-items: center;
  gap: 10px;
}

.search-input {
  border: 1px solid #d9d9d9;
  height: 32px;
  padding: 0 12px;
  border-radius: 6px;
  font-size: 14px;
  width: 300px;
  outline: none;
  transition: all 0.2s;
}

.search-input:focus {
  border-color: #1890ff;
  box-shadow: 0 2px 8px rgba(24,144,255,0.2);
}

.search-button, .reset-button {
  background-color: #1890ff;
  color: white;
  border: none;
  height: 32px;
  padding: 0 16px;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
}

.search-button:hover, .reset-button:hover {
  background-color: #4096ff;
}

.reset-button {
  background-color: #f5f5f5;
  color: #666;
  border: 1px solid #d9d9d9;
}

.reset-button:hover {
  background-color: #e6f7ff;
  border-color: #1890ff;
  color: #1890ff;
}

/* 筛选区域 */
.filter-section {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  padding: 15px;
  background-color: #fafafa;
  border-radius: 8px;
}

.filter-label {
  font-weight: 500;
  color: #333;
  margin-right: 15px;
  white-space: nowrap;
}

.category-filters {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.category-filter {
  padding: 6px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 16px;
  cursor: pointer;
  font-size: 12px;
  background-color: white;
  transition: all 0.2s;
  white-space: nowrap;
}

.category-filter:hover {
  border-color: #1890ff;
  color: #1890ff;
}

.category-filter.active {
  background-color: #1890ff;
  border-color: #1890ff;
  color: white;
}

/* 统计卡片 */
.inventory-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-bottom: 20px;
}

.stat-card {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 20px;
  border-radius: 10px;
  text-align: center;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
}

.stat-card:nth-child(2) {
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
}

.stat-card:nth-child(3) {
  background: linear-gradient(135deg, #ff9a56 0%, #ff6a88 100%);
}

.stat-label {
  font-size: 14px;
  opacity: 0.9;
  margin-bottom: 8px;
}

.stat-value {
  font-size: 24px;
  font-weight: bold;
}

.stat-value.warning {
  color: #ffeb3b;
}

/* 表格 */
.inventory-table-container {
  background-color: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  width: 100%;
}

.inventory-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

.inventory-table th {
  background-color: #fafafa;
  padding: 16px 12px;
  text-align: left;
  font-weight: 600;
  color: #333;
  border-bottom: 2px solid #e8e8e8;
  font-size: 14px;
  white-space: nowrap;
}

.inventory-table th:nth-child(1) {
  width: 30%;
}

.inventory-table th:nth-child(2) {
  width: 20%;
}

.inventory-table th:nth-child(3) {
  width: 15%;
}

.inventory-table th:nth-child(4) {
  width: 15%;
}

.inventory-table th:nth-child(5) {
  width: 20%;
}

.sortable-header {
  cursor: pointer;
  user-select: none;
  position: relative;
  transition: background-color 0.2s;
}

.sortable-header:hover {
  background-color: #f0f0f0;
}

.sort-indicator {
  margin-left: 8px;
  font-size: 12px;
  color: #666;
}

.sort-asc {
  color: #52c41a;
}

.sort-desc {
  color: #f5222d;
}

.sort-none {
  color: #999;
}

.inventory-table td {
  padding: 12px;
  border-bottom: 1px solid #f0f0f0;
  font-size: 13px;
}

.inventory-table tr:hover {
  background-color: #f9f9f9;
}

.product-info {
  min-width: 150px;
}

.product-name {
  font-weight: 500;
  color: #333;
  line-height: 1.4;
}

.product-code {
  color: #999;
  font-size: 12px;
  font-family: monospace;
}

.category-tag {
  background-color: #e6f7ff;
  color: #1890ff;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
}

.stock-cell {
  text-align: center;
  font-weight: 600;
}

.stock-value.low-stock {
  color: #f5222d;
}

.price-cell {
  text-align: right;
  font-weight: 600;
  color: #e74c3c;
}

.no-data {
  text-align: center;
  color: #999;
  padding: 40px;
  font-size: 14px;
}

/* 分页 */
.pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-top: 20px;
  padding: 20px;
  background-color: #fafafa;
  border-radius: 8px;
  box-sizing: border-box;
}

.pagination-info {
  color: #666;
  font-size: 14px;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 15px;
}

.page-btn {
  background-color: white;
  border: 1px solid #d9d9d9;
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 14px;
}

.page-btn:hover:not(:disabled) {
  border-color: #1890ff;
  color: #1890ff;
}

.page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-number {
  font-weight: 500;
  color: #333;
}
</style>